import { action, computed, observable, runInAction } from "mobx";
import qs from "qs";
import moment from "moment";

import BrowseStore from "./browse";
import CommonStore from "./common";
import { getQuery } from "../service/http";
import { goLogin } from "../service/utils";

const isDev =
  typeof window !== "undefined" && window.location.hostname === "localhost";

const EMPTY_FILTER_PARAMS = {
  order_by: [],
  tags: [],
  cuisines_categories: [],
  price_range: [],
  distance: [],
  locations: [],
  landmarks: [],
  cuisines: [],
  extras: [],
};
/**
 * 计算两个坐标点之间的距离（米）
 * 使用 Haversine 公式
 */
function calculateDistance(lat1, lng1, lat2, lng2) {
  const R = 6371000; // 地球半径（米）
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function getCachePos() {
  return null;
  try {
    return JSON.parse(localStorage.getItem("pos"));
  } catch (error) {
    return null;
  }
}

function normalizeArray(value) {
  if (!Array.isArray(value)) return [];
  return value.filter((v) => v != null && v !== "");
}

function arrayEqual(a, b) {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}
const extendDate = (dateStr) => {
  if (!dateStr) return null;
  const date = moment(dateStr);
  if (!date.isValid()) return null;
  return [
    date.startOf("day").format("YYYY-MM-DD HH:mm:ss"),
    date.endOf("day").format("YYYY-MM-DD HH:mm:ss"),
  ];
};
const paramsBuilder = (params) => {
  const nextParams = {};
  Object.keys(params || {}).forEach((key) => {
    const value = params[key];
    if (Array.isArray(value)) {
      if (value.length > 0) nextParams[key] = value;
      return;
    }
    if (value === "" || value == null) return;
    nextParams[key] = value;
  });
  return nextParams;
};

function maxNum(num) {
  if (num > 99) return "99+";
  return num;
}

class Store {
  history = null;
  _totalFetchTimer = null;
  _totalLoadingTimer = null;
  _totalRequestSeq = 0;
  _initialized = false;

  // Track the last region value coming from URL sync.
  // This avoids missing the change when other actions update `this.region`
  // before the router location sync happens.
  _lastSyncedRegion = null;

  // Router snapshot (drives activeTab + URL sync)
  @observable pathname = "";

  // Query-derived base fields
  // NOTE: region 是统一的区域标识，getQuery() 已经将 region_id/regionId 合并到 region
  @observable region = "";
  @observable lang = "";
  @observable private_token = "";

  @observable order_by = "";

  // Browse filter state (extracted from pages/browse)
  @observable state = {
    distance: "",
    "location.id": [],
    "landmarks.id": [],
    "tags.id": [],
    "cuisines.id": [],
    "extras.id": [],
    avg_price: "",
  };

  @observable fakeState = {
    distance: "",
    "location.id": [],
    "landmarks.id": [],
    "tags.id": [],
    "cuisines.id": [],
    "extras.id": [],
    avg_price: "",
  };

  @observable mapRange = null;
  @observable fakeRange = null;
  @observable isInFakeMode = false;

  // Head-bar search state
  @observable searchState = {
    seats: "",
    date: "",
    q: "",
  };

  @observable searchTrigger = 0;
  @observable exploreMapMode = false;

  // Shared region/city data
  @observable regionList = [];
  @observable currentCity = null;
  @observable pageCity = {};
  @observable searchRegionList = [];

  // Keep the old ref-like API surface for components.
  @observable pos = getCachePos();
  @observable logo = null;

  // Filter parameters + derived counts
  @observable filterParams = EMPTY_FILTER_PARAMS;
  @observable paramsLoading = false;

  // Total count for "See X restaurants" in filters
  @observable loading = false;
  @observable total = 0;

  @computed
  get activeTab() {
    if (this.pathname === "/home") return "home";
    if (this.pathname === "/browse") return "explore";
    return this.pathname;
  }

  @computed
  get sums() {
    const state = this.isInFakeMode ? this.fakeState : this.state;
    const location =
      normalizeArray(state["location.id"]).length +
      normalizeArray(state["landmarks.id"]).length +
      (state.distance ? 1 : 0);
    const cuisines =
      normalizeArray(state["cuisines.id"]).length +
      normalizeArray(state["extras.id"]).length;

    return {
      order_by: 0,
      filters: maxNum(
        location +
          cuisines +
          normalizeArray(state["tags.id"]).length +
          (state.avg_price ? 1 : 0),
      ),
      location: maxNum(location),
      cuisines: maxNum(cuisines),
    };
  }

  @action.bound
  attachRouter(history) {
    this.history = history;
  }

  @action.bound
  setOrderBy(orderBy) {
    this.order_by = orderBy;
  }

  @action.bound
  setMapRange(range) {
    this.fakeRange = range;
    this.mapRange = range;
    if (this.isInFakeMode) {
      // 只在筛选弹窗打开时（isInFakeMode为true）才触发 scheduleTotalFetch
      this.scheduleTotalFetch();
    }
  }
  @action.bound
  setLogo() {
    CommonStore.getFooterDetail(this.region).then((data) => {
      this.logo = data;
    });
  }

  @action.bound
  setFakeRange(range) {
    this.fakeRange = range;
  }

  @action.bound
  syncFromLocation(routerLocation) {
    const urlSearch = getQuery();

    if (routerLocation?.pathname && this.pathname !== routerLocation.pathname) {
      this.pathname = routerLocation.pathname;
    }
    if (typeof document !== "undefined" && routerLocation?.pathname) {
      document.body.setAttribute("path", routerLocation.pathname);
    }

    const nextLang = urlSearch.lang || "";
    // getQuery() 已经统一处理了 region/region_id/regionId
    const nextRegion = urlSearch.region || "";
    const nextToken = urlSearch.private_token || "";

    const prevSyncedRegion =
      this._lastSyncedRegion == null ? this.region : this._lastSyncedRegion;
    const regionChanged = prevSyncedRegion !== nextRegion;

    const q = urlSearch.q == null ? "" : String(urlSearch.q);
    const seats = urlSearch.seats == null ? "" : String(urlSearch.seats);
    const date = urlSearch.date == null ? "" : String(urlSearch.date);

    // Query-derived top-level fields
    if (this.lang !== nextLang) this.lang = nextLang;
    if (this.region !== nextRegion) {
      this.region = nextRegion;
    }
    if (this.private_token !== nextToken) this.private_token = nextToken;

    // Record after applying URL-derived fields.
    this._lastSyncedRegion = nextRegion;

    // Each route change should reset searchState to reflect query params.
    this.setSearchState({
      seats,
      date,
      q,
    });

    if (regionChanged) {
      this.cleanFilter();
      if (nextRegion) this.fetchPageCityByRegion(nextRegion);
      this.fetchFilterParams();
    }

    if (this.activeTab === "explore") {
      // this.scheduleTotalFetch();
    }

    // Leaving /browse should reset map mode back to list.
    if (this.activeTab !== "explore" && this.exploreMapMode) {
      this.exploreMapMode = false;
    }
  }

  @action.bound
  init() {
    if (this._initialized) return;
    this._initialized = true;

    const urlSearch = getQuery();
    this.getPosition();
    runInAction(() => {
      this.lang = urlSearch.lang || this.lang || "";
      // getQuery() 已经统一处理了 region/region_id/regionId
      this.region = urlSearch.region || this.region || "";
      this.private_token = urlSearch.private_token || this.private_token || "";
      this.searchState = {
        ...this.searchState,
        seats: urlSearch.seats == null ? "" : String(urlSearch.seats),
        date: urlSearch.date == null ? "" : String(urlSearch.date),
        q: urlSearch.q == null ? "" : String(urlSearch.q),
      };
    });
    this._lastSyncedRegion = this.region;
    if (this.region) this.fetchPageCityByRegion(this.region);
    this.fetchRegions();
    this.fetchFilterParams();
    this.setLogo();
  }

  @action.bound
  setExploreMapMode(next) {
    this.exploreMapMode = !!next;
    this.mapRange = null;
    this.fakeRange = null;
  }

  @action.bound
  setPageCity(city) {
    this.pageCity = city || {};
  }

  @action.bound
  setState(updater) {
    const prev = this.state;
    const patch = typeof updater === "function" ? updater(prev) : updater;
    const nextPatch = patch || {};

    let changed = false;
    let locationChanged = false;
    const next = { ...prev };
    Object.keys(nextPatch).forEach((key) => {
      const nextVal = nextPatch[key];
      const prevVal = prev[key];

      if (Array.isArray(nextVal) || Array.isArray(prevVal)) {
        if (arrayEqual(prevVal, nextVal)) return;
        next[key] = Array.isArray(nextVal) ? [...nextVal] : nextVal;
        changed = true;
        if (key === "location.id" || key === "landmarks.id") {
          locationChanged = true;
        }
        return;
      }

      if (prevVal === nextVal) return;
      next[key] = nextVal;
      changed = true;
    });

    if (!changed) return;
    this.state = next;
    if (locationChanged) {
      this.mapRange = null;
    }
  }

  @action.bound
  cleanFakeState(type) {
    if (type === "location") {
      this.setFakeState({
        ...this.fakeState,
        distance: "",
        "location.id": [],
        "landmarks.id": [],
      });
    } else if (type === "cuisines") {
      this.fakeState = {
        ...this.fakeState,
        "cuisines.id": [],
      };
    } else {
      this.setFakeState({
        distance: "",
        "location.id": [],
        "landmarks.id": [],
        "tags.id": [],
        "cuisines.id": [],
        "extras.id": [],
        avg_price: "",
      });
    }
    if (this.isInFakeMode) {
      this.scheduleTotalFetch();
    }
  }

  @action.bound
  setFakeState(updater) {
    const prev = this.fakeState;
    const old = this.state;
    const patch = typeof updater === "function" ? updater(prev) : updater;
    const nextPatch = patch || {};

    let changed = false;
    let locationChanged = false;
    const next = { ...prev };
    Object.keys(nextPatch).forEach((key) => {
      const nextVal = nextPatch[key];
      const prevVal = prev[key];

      if (Array.isArray(nextVal) || Array.isArray(prevVal)) {
        if (arrayEqual(prevVal, nextVal)) return;
        next[key] = Array.isArray(nextVal) ? [...nextVal] : nextVal;
        changed = true;
        if (key === "location.id" || key === "landmarks.id") {
          locationChanged = true;
        }
        return;
      }

      if (prevVal === nextVal) return;
      next[key] = nextVal;
      changed = true;
    });

    if (!changed) return;
    this.fakeState = next;
    if (locationChanged) {
      const hasOldLocations =
        old["location.id"]?.length > 0 || old["landmarks.id"]?.length > 0;
      const noNewLocals =
        next["location.id"]?.length === 0 && next["landmarks.id"]?.length === 0;
      if (noNewLocals) {
        this.fakeRange = hasOldLocations ? {} : { ...this.mapRange };
      } else {
        this.fakeRange = {};
      }
    }
    this.scheduleTotalFetch();
  }

  @action.bound
  setIsInFakeMode(value) {
    if (value && !this.isInFakeMode) {
      this.scheduleTotalFetch();
    }
    this.isInFakeMode = !!value;
  }

  @action.bound
  setSearchState(next) {
    this.searchState = {
      ...this.searchState,
      ...next,
    };
  }

  @action.bound
  bumpSearchTrigger() {
    this.searchTrigger += 1;
  }

  @action.bound
  cleanFilter() {
    this.state = {
      ...this.state,
      distance: "",
      "location.id": [],
      "landmarks.id": [],
      "tags.id": [],
      "cuisines.id": [],
      "extras.id": [],
      avg_price: "",
    };
    this.mapRange = null;
    this.cleanFakeState();
  }

  @action.bound
  cleanMapRange() {
    this.mapRange = null;
    this.onSearchBtnClick(this.searchState.q);
  }

  @action.bound
  cleanFilterAndSearch() {
    this.state = {
      ...this.state,
      distance: "",
      "location.id": [],
      "landmarks.id": [],
      "tags.id": [],
      "cuisines.id": [],
      "extras.id": [],
      avg_price: "",
    };
    this.searchState = {
      seats: "",
      date: "",
      q: "",
    };
    this.mapRange = null;
    this.cleanFakeState();
    this.onSearchBtnClick();
  }

  getSearch(params = {}) {
    const {
      region = "",
      lang = "",
      gid = "",
      private_token = "",
      callback = "",
      payment = "",
      login_url = "",
      source = "",
    } = getQuery();

    const obj = {
      gid,
      lang,
      region,
      private_token,
      callback,
      payment,
      login_url,
      source,
      ...params,
    };

    Object.keys(obj).forEach((key) => {
      if (obj[key] === "" || obj[key] == null || obj[key] == "undefined")
        obj[key] = null;
    });

    return qs.stringify(obj, { skipNulls: true });
  }

  @action.bound
  onHomeClick() {
    const { lang, region } = getQuery();

    const pageCityDomain = this.pageCity?.domain;
    let base = pageCityDomain
      ? `https://www.${pageCityDomain}/`
      : document?.referrer;
    if (isDev) base = "http://localhost:5500/";

    this.setSearchState({
      seats: "",
      date: "",
      q: "",
    });

    this.order_by = "";
    this.cleanFilter();

    if (this.activeTab === "home") return;

    window.location.href = `/#/home?${this.getSearch({})}`;

    if (typeof window !== "undefined" && window.top) {
      window.top.postMessage(
        {
          type: "other",
          params: {
            url: region && lang ? `${base}${lang}/${region}` : base,
          },
        },
        "*",
      );
    }
  }

  @action.bound
  onExploreClick() {
    const nextRegion = this.region || "shanghai";

    this.history.push(`/browse?${this.getSearch()}`);
    let obj = {
      utf8: 1,
      seats: getQuery().seats || "",
      date: getQuery().date || "",
      q: getQuery().q || "",
      region: nextRegion,
    };
    this.setSearchState({
      seats: getQuery().seats || "",
      date: getQuery().date || "",
      q: getQuery().q || "",
    });
    const base = this.pageCity?.domain
      ? `https://www.${this.pageCity?.domain}/`
      : document?.referrer;

    window.top.postMessage(
      {
        type: "other",
        params: {
          url: `${base}${
            this.lang
          }/${nextRegion}/search_restaurants?${qs.stringify(obj, {
            skipNulls: true,
          })}`,
        },
      },
      "*",
    );
  }

  @action.bound
  onUserClick() {
    const pageCityDomain = this.pageCity?.domain;
    const { lang, region } = getQuery();
    if (!this.private_token) return goLogin();

    this.history.push(`/profile?${this.getSearch({})}`);
    this.cleanFilter();

    const base = pageCityDomain
      ? `https://www.${pageCityDomain}/`
      : document?.referrer;
    const url = `${base}${lang}/members/bookings`;
    window.top.postMessage(
      {
        type: "other",
        params: { url, region, lang },
      },
      "*",
    );
  }

  @action.bound
  onCityClick(city) {
    this.setPageCity(city);
    const nextRegion = city?.key_word;
    if (nextRegion) {
      this.region = nextRegion;
      this.setLogo(nextRegion);
    }
    this.order_by = "";
    this.cleanFilter();
    if (this.activeTab === "explore") {
      const search = this.getSearch({
        q: undefined,
        seats: undefined,
        date: undefined,
        region: nextRegion,
      });
      const search2 = qs.stringify(
        {
          q: undefined,
          seats: undefined,
          date: undefined,
          region: nextRegion,
        },
        { skipNulls: true },
      );
      this.history.replace(`/browse?${search}`);
      return window.top.postMessage(
        {
          type: "other",
          params: {
            url: `https://www.${city.domain}/${this.lang}/${nextRegion}/search_restaurants?${search2}`,
          },
        },
        "*",
      );
    } else {
      this.history.replace(
        `/home?${this.getSearch({
          region: nextRegion,
        })}`,
      );
      window.top.postMessage(
        {
          type: "switch",
          params: {
            url: `https://www.${city.domain}/${this.lang}/${nextRegion}`,
          },
        },
        "*",
      );
    }
  }

  @action.bound
  onSearchBtnClick(keyword, city) {
    this.bumpSearchTrigger();
    const nextRegion = city || this.region;
    this.history.push(
      `/browse?${this.getSearch({
        q: keyword,
        seats: this.searchState.seats,
        date: this.searchState.date,
        region: nextRegion,
      })}`,
    );
    let obj = {
      utf8: 1,
      q: keyword,
      seats: this.searchState.seats,
      date: this.searchState.date,
    };

    Object.keys(obj).forEach((key) => {
      if (obj[key] === "" || obj[key] == null) obj[key] = null;
    });

    window.top.postMessage(
      {
        type: "other",
        params: {
          region: nextRegion,
          lang: this.lang,
          url: `${
            this.pageCity?.domain
              ? `https://www.${this.pageCity?.domain}/`
              : document?.referrer
          }${this.lang}/${nextRegion}/search_restaurants?${qs.stringify(obj, {
            skipNulls: true,
          })}`,
        },
      },
      "*",
    );
  }

  @action.bound
  searchRegion(keyword) {
    CommonStore.searchRegion(keyword).then((res) => {
      runInAction(() => {
        this.searchRegionList = res || [];
      });
    });
  }

  @action.bound
  getPosition() {
    if (typeof navigator === "undefined" || !navigator.geolocation) return;

    if (this.pos) {
      CommonStore.getCityByLocation(this.pos.lat, this.pos.lng).then((res) => {
        runInAction(() => {
          this.currentCity = res;
        });
      });
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        let mockPos = isDev
          ? {
              latitude: 31.214293127420397,
              longitude: 121.45227095628326,
            }
          : null;
        const { latitude: lat, longitude: lng } = mockPos || position.coords;
        localStorage.setItem(
          "pos",
          JSON.stringify({ lat, lng, time: Date.now() }),
        );
        this.pos = { lat, lng };
        this.bumpSearchTrigger();
        CommonStore.getCityByLocation(lat, lng).then((res) => {
          runInAction(() => {
            this.currentCity = res;
          });
        });
      },
      () => {},
      {
        timeout: 3500,
        enableHighAccuracy: true,
        maximumAge: 75000,
      },
    );
  }

  @action.bound
  fetchRegions() {
    CommonStore.getAllRegions().then((res) => {
      if (!res) return;
      const next = (res || []).map((item) => {
        const mapRegions = {};
        (item.regions || []).forEach((city) => {
          const char = city?.key_word?.[0]?.toLocaleUpperCase();
          if (!char) return;
          if (!mapRegions[char]) mapRegions[char] = [];
          mapRegions[char].push(city);
        });
        return { ...item, regions: mapRegions };
      });

      runInAction(() => {
        this.regionList = next;
      });
    });
  }

  @action.bound
  fetchPageCityByRegion(region) {
    if (!region) return;
    // NOTE: CommonStore.getCityByRegion caches the first cityDetail and may return
    // stale results after switching regions. Use BrowseStore.getCityByRegion here
    // to always reflect the selected region.
    BrowseStore.getCityByRegion(region).then((data) => {
      if (!data) return;
      runInAction(() => {
        this.pageCity = data;
      });
    });
  }

  @action.bound
  fetchFilterParams() {
    if (!this.lang || !this.region) return;
    this.paramsLoading = true;
    BrowseStore.getBrowseParams({
      lang: this.lang,
      region_id: this.region,
    })
      .then((data) => {
        runInAction(() => {
          this.filterParams = data || EMPTY_FILTER_PARAMS;
        });
      })
      .catch(() => {
        // ignore
      })
      .then(
        action(() => {
          this.paramsLoading = false;
        }),
      );
  }

  @action.bound
  scheduleTotalFetch() {
    if (this._totalFetchTimer) clearTimeout(this._totalFetchTimer);
    if (this._totalLoadingTimer) clearTimeout(this._totalLoadingTimer);

    const requestSeq = (this._totalRequestSeq += 1);

    this._totalLoadingTimer = setTimeout(
      action(() => {
        // Guard against stale timers from previous schedules.
        if (this._totalRequestSeq !== requestSeq) return;
        this.loading = true;
      }),
      300,
    );

    this._totalFetchTimer = setTimeout(() => {
      this.fetchTotalCount(requestSeq);
    }, 200);
  }

  @action.bound
  fetchTotalCount(requestSeq) {
    if (!this.lang || !this.region) {
      if (this._totalLoadingTimer) {
        clearTimeout(this._totalLoadingTimer);
        this._totalLoadingTimer = null;
      }
      this.total = 0;
      this.loading = false;
      return;
    }

    const seq = requestSeq || (this._totalRequestSeq += 1);

    const urlSearch = getQuery();
    const hasLocationFilter =
      this.fakeState.distance ||
      this.fakeState["location.id"].length > 0 ||
      this.fakeState["landmarks.id"].length > 0;
    const mapRange = hasLocationFilter
      ? {}
      : this.isInFakeMode
        ? this.fakeRange
        : this.mapRange;
    const params = {
      region: this.region,
      page: 1,
      per_page: 1,
      ...paramsBuilder({
        ...this.fakeState,
        ...this.pos,
        ...mapRange,
        q: urlSearch.q,
        date: null,
        datetime: extendDate(urlSearch.date),
        seats: urlSearch.seats,
      }),
    };

    const finalize = action(() => {
      // Only the latest scheduled request may mutate loading state.
      if (this._totalRequestSeq !== seq) return;
      if (this._totalLoadingTimer) {
        clearTimeout(this._totalLoadingTimer);
        this._totalLoadingTimer = null;
      }
      this.loading = false;
    });

    BrowseStore.getBrowseRestaurants(params)
      .then((res) => {
        runInAction(() => {
          if (this._totalRequestSeq !== seq) return;
          const raw = res?.headers?.total_count;
          const n = raw == null ? 0 : Number(raw);
          this.total = Number.isFinite(n) ? n : 0;
        });
      })
      .catch(() => {
        runInAction(() => {
          if (this._totalRequestSeq !== seq) return;
          this.total = 0;
        });
      })
      .then(finalize);
  }
}

export default new Store();
